import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../core/api.service';
import { Fornecedor } from '../../core/models';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf],
  template: `
    <header class="page-header">
      <div><h2>Fornecedores</h2><p>Cadastro das empresas e pessoas que fornecem produtos ou serviços</p></div>
      <button type="button" (click)="novoFornecedor()">Novo fornecedor</button>
    </header>

    <div class="toast-stack" *ngIf="erro || mensagem">
      <div class="toast" [class.toast-error]="erro" [class.toast-success]="!erro && mensagem" role="alert">
        <span>{{ erro || mensagem }}</span><button class="toast-close" type="button" (click)="fecharAviso()" aria-label="Fechar mensagem">X</button>
      </div>
    </div>

    <section class="panel" *ngIf="aba === 'cadastro'">
      <div class="panel-header"><div><h3>{{ form.controls.id_fornecedor.value ? 'Editar fornecedor' : 'Novo fornecedor' }}</h3><p class="field-hint">Fornecedores ativos ficam disponíveis para vinculação nas contas a pagar.</p></div></div>
      <form class="grid-form" [formGroup]="form" (ngSubmit)="salvar()" novalidate>
        <input type="hidden" formControlName="id_fornecedor">
        <label>Nome ou razão social<input formControlName="nome" placeholder="Ex.: Empresa de materiais Ltda."></label>
        <label>CNPJ<input formControlName="cnpj" inputmode="numeric" placeholder="Opcional"></label>
        <label>Contato<input formControlName="contato"></label>
        <label>Telefone<input formControlName="telefone" inputmode="tel"></label>
        <label>Email<input type="email" formControlName="email"></label>
        <label>Endereço<input formControlName="endereco"></label>
        <label class="check"><input type="checkbox" formControlName="ativo"> Ativo</label>
        <label class="full">Observação<textarea formControlName="observacao" rows="3"></textarea></label>
        <div class="form-actions full"><button type="submit" [disabled]="form.invalid">Salvar fornecedor</button><button class="ghost" type="button" (click)="limpar()">Limpar</button><button class="ghost" type="button" (click)="aba = 'consulta'">Voltar</button></div>
      </form>
    </section>

    <section class="panel" *ngIf="aba === 'consulta'">
      <div class="panel-header"><div><h3>Fornecedores cadastrados</h3><p class="field-hint">Fornecedores inativos não aparecem nas novas contas a pagar.</p></div></div>
      <table class="data-table" *ngIf="fornecedores.length; else semFornecedores">
        <thead><tr><th>Fornecedor</th><th>CNPJ</th><th>Contato</th><th>Endereço</th><th>Status</th><th>Ações</th></tr></thead>
        <tbody><tr *ngFor="let fornecedor of fornecedores">
          <td data-label="Fornecedor"><strong>{{ fornecedor.nome }}</strong><span class="muted-line" *ngIf="fornecedor.email">{{ fornecedor.email }}</span></td>
          <td data-label="CNPJ">{{ fornecedor.cnpj || '-' }}</td>
          <td data-label="Contato">{{ fornecedor.contato || '-' }}<span class="muted-line" *ngIf="fornecedor.telefone">{{ fornecedor.telefone }}</span></td>
          <td data-label="Endereço">{{ fornecedor.endereco || '-' }}</td>
          <td data-label="Status"><span class="status" [class.cancelada]="!fornecedor.ativo">{{ fornecedor.ativo ? 'Ativo' : 'Inativo' }}</span></td>
          <td class="actions" data-label="Ações"><button type="button" (click)="editar(fornecedor)">Editar</button><button type="button" (click)="inativar(fornecedor)" [disabled]="!fornecedor.ativo">Inativar</button></td>
        </tr></tbody>
      </table>
      <ng-template #semFornecedores><p class="field-hint">Nenhum fornecedor cadastrado.</p></ng-template>
    </section>
  `
})
export class FornecedoresComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);
  fornecedores: Fornecedor[] = [];
  aba: 'consulta' | 'cadastro' = 'consulta';
  erro = '';
  mensagem = '';
  form = this.fb.nonNullable.group({
    id_fornecedor: [0], nome: ['', Validators.required], cnpj: [''], contato: [''], telefone: [''],
    email: ['', Validators.email], endereco: [''], observacao: [''], ativo: [true]
  });

  ngOnInit() { this.carregar(); }

  carregar() { this.api.fornecedores().subscribe({ next: (fornecedores) => (this.fornecedores = fornecedores), error: (error) => this.mostrarErro(error) }); }
  novoFornecedor() { this.limpar(); this.aba = 'cadastro'; }
  editar(fornecedor: Fornecedor) { this.form.patchValue({ id_fornecedor: fornecedor.id_fornecedor, nome: fornecedor.nome, cnpj: fornecedor.cnpj || '', contato: fornecedor.contato || '', telefone: fornecedor.telefone || '', email: fornecedor.email || '', endereco: fornecedor.endereco || '', observacao: fornecedor.observacao || '', ativo: fornecedor.ativo }); this.aba = 'cadastro'; }
  salvar() {
    this.form.markAllAsTouched();
    if (this.form.invalid) { this.erro = 'Preencha o nome e revise o email do fornecedor.'; return; }
    const data = this.form.getRawValue();
    this.api.salvarFornecedor({ ...data, id_fornecedor: data.id_fornecedor || undefined, cnpj: data.cnpj.replace(/\D/g, ''), email: data.email.trim().toLowerCase() }).subscribe({ next: (fornecedor) => { this.mensagem = `Fornecedor ${fornecedor.nome} salvo com sucesso.`; this.limpar(); this.aba = 'consulta'; this.carregar(); }, error: (error) => this.mostrarErro(error) });
  }
  inativar(fornecedor: Fornecedor) { if (!window.confirm(`Inativar o fornecedor "${fornecedor.nome}"?`)) return; this.api.inativarFornecedor(fornecedor.id_fornecedor).subscribe({ next: () => { this.mensagem = 'Fornecedor inativado.'; this.carregar(); }, error: (error) => this.mostrarErro(error) }); }
  limpar() { this.form.reset({ id_fornecedor: 0, nome: '', cnpj: '', contato: '', telefone: '', email: '', endereco: '', observacao: '', ativo: true }); }
  fecharAviso() { this.erro = ''; this.mensagem = ''; }
  private mostrarErro(error: any) { this.mensagem = ''; this.erro = error?.error?.message || error?.message || 'Não foi possível concluir a operação.'; }
}
